import React from 'react';
import { BROKERAGES } from '../lib/brokerages';
import NumField from './NumField';

// A standalone card used in Step 1 (your current brokerage) and Step 2 (compare options).
export default function SlotCard({
  slot, onUpdate, onBrokerageChange, onRemove, isCurrent = false,
}) {
  const b = BROKERAGES[slot.brokerage_key];

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex flex-col gap-4"
      data-testid={isCurrent ? 'bta-slot-current' : 'bta-slot-option'}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={
                'text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded ' +
                (isCurrent
                  ? 'bg-[#DCD9C8] text-[#092825]'
                  : 'bg-[#2FA163]/10 text-[#286C4E]')
              }
            >
              {isCurrent ? 'Your current' : 'Option'}
            </span>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Name this option — tap to rename
            </span>
            <input
              value={slot.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              data-testid="bta-slot-name-input"
              placeholder="e.g., eXp Realty, KW Bradenton, Compass"
              className="text-lg font-bold text-[#092825] w-full border border-slate-300 rounded px-3 py-2 hover:border-slate-400 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163]/30 focus:outline-none transition-colors"
            />
          </label>
        </div>
        {!isCurrent && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            data-testid="bta-slot-remove-btn"
            className="text-slate-400 hover:text-red-500 text-sm"
          >
            Remove
          </button>
        )}
      </div>

      <label className="block text-sm">
        <span className="text-slate-700 font-medium">Brokerage</span>
        <select
          className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
          value={slot.brokerage_key}
          onChange={(e) => onBrokerageChange(e.target.value)}
          data-testid="bta-slot-brokerage-select"
        >
          {Object.entries(BROKERAGES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </label>
      <p className="text-xs text-slate-500 -mt-2 italic">{b.note}</p>

      <div className="grid grid-cols-2 gap-3">
        <NumField label="Agent split %" suffix="%" value={slot.agent_split_pct}
          onChange={(v) => onUpdate({ agent_split_pct: v })}
          help="The percentage of each commission you keep before hitting your annual cap. An 80% split means you keep $80 of every $100 in commission and your brokerage keeps $20." />
        <NumField label="Annual cap" prefix="$" value={slot.annual_cap}
          onChange={(v) => onUpdate({ annual_cap: v })}
          hint={slot.annual_cap === 0 ? '0 = 100% model' : ''}
          help="The maximum dollars your brokerage can take from you in a year from splits. Once you've paid this much, you keep 100% of the brokerage portion on every deal after. Enter 0 for a flat-fee / 100% commission model." />
        <NumField label="Post-cap fee (/deal)" prefix="$" value={slot.post_cap_fee}
          onChange={(v) => onUpdate({ post_cap_fee: v })}
          help="A flat dollar amount your brokerage charges per closed transaction once you've capped for the year. Common at cap-model brokerages like eXp and Real (typically $285/deal)." />
        <NumField label="Monthly fees" prefix="$" value={slot.monthly_fees}
          onChange={(v) => onUpdate({ monthly_fees: v })}
          help="Any recurring monthly fee you pay to the brokerage — tech fees, desk fees, subscription dues. We multiply this by 12 for the annual projection." />
        <NumField label="Annual E&O" prefix="$" value={slot.annual_eo}
          onChange={(v) => onUpdate({ annual_eo: v })}
          help="Errors & Omissions insurance cost per year. Some brokerages charge this annually, others amortize per-transaction — enter whichever best reflects your real cost." />
        <NumField label="Already paid to cap" prefix="$" value={slot.already_paid_to_cap}
          onChange={(v) => onUpdate({ already_paid_to_cap: v })}
          hint="This year, YTD"
          help="How many dollars you've already paid toward your cap this year. Only use this for your CURRENT brokerage if you're mid-year. A fresh start at a new brokerage should leave this at 0." />
      </div>

      {slot.is_kw && (
        <NumField label="KW royalty cap" prefix="$" value={slot.kw_royalty_cap}
          onChange={(v) => onUpdate({ kw_royalty_cap: v })}
          help="Keller Williams takes a 6% franchise royalty off your gross commission, capped at this dollar amount per year. $3,000 is typical. This is separate from — and in addition to — your brokerage split cap above." />
      )}

      <div className="border-t border-slate-200 pt-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => onUpdate({ on_team: !slot.on_team })}
            data-testid="bta-slot-team-toggle"
            className={
              (slot.on_team ? 'bg-[#2FA163]' : 'bg-slate-300') +
              ' relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors'
            }
          >
            <span
              className={
                (slot.on_team ? 'translate-x-5' : 'translate-x-0.5') +
                ' inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5'
              }
            />
          </button>
          <div className="text-sm">
            <div className="font-medium text-slate-700">On a team at this brokerage?</div>
            <div className="text-xs text-slate-500">Layer a team split on top of the brokerage fees</div>
          </div>
        </div>
      </div>

      {slot.on_team && (
        <div className="bg-slate-50 -mx-6 -mb-6 px-6 pb-6 pt-4 border-t border-slate-200 rounded-b-lg">
          <label className="block text-sm">
            <span className="text-slate-700 font-medium">Team name</span>
            <input
              type="text"
              value={slot.team_name ?? ''}
              onChange={(e) => onUpdate({ team_name: e.target.value })}
              placeholder="e.g., The Smith Group"
              className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <NumField label="Team split % (to team)" suffix="%" value={slot.team_split_pct}
              onChange={(v) => onUpdate({ team_split_pct: v })}
              help="The percentage of the commission the team takes. For example, a 30% team split means the team keeps 30 cents of every dollar of gross commission before (or after) the brokerage split — the order is set by the waterfall below." />
            <NumField label="Team cap (0 = none)" prefix="$" value={slot.team_cap}
              onChange={(v) => onUpdate({ team_cap: v })}
              help="The maximum dollars the team will take from you in a year. Once you hit it, the team stops taking its split. Enter 0 if the team doesn't cap — they'll take their split on every deal all year." />
            <NumField label="Team monthly fee" prefix="$" value={slot.team_monthly_fee}
              onChange={(v) => onUpdate({ team_monthly_fee: v })}
              help="Any recurring monthly fee the team charges you — CRM access, lead subscription, marketing contribution. Multiplied by 12 for the annual total." />
            <NumField label="Team per-deal fee" prefix="$" value={slot.team_per_deal_fee}
              onChange={(v) => onUpdate({ team_per_deal_fee: v })}
              help="Any flat fee the team charges per closed transaction — admin fee, lead fee, TC fee paid to the team. Separate from the team split percentage." />
          </div>
          <div className="mt-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Split waterfall order</span>
              <select
                className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
                value={slot.waterfall}
                onChange={(e) => onUpdate({ waterfall: e.target.value })}
                data-testid="bta-slot-waterfall-select"
              >
                <option value="team_first">Team cut first, then brokerage split (default)</option>
                <option value="brokerage_first">Brokerage split first, then team cut</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
